import * as React from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { styled } from "@mui/material/styles";
import { LabelDot, SingleLegendWrapper } from "./Styled";
import { api } from "../../../../../shared/hooks/api";
import { PRODUCT_ORDER_STATUS, SALE_ORDER_STATUS } from "../../../../../data/enums";
import PageLoader from "../../../../../shared/components/LoadingPage";

const size = {
    height: 200,
};

const StyledText = styled("text")(({ theme }) => ({
    fill: theme.palette.text.primary,
    textAnchor: "middle",
    dominantBaseline: "central",
    fontSize: 20,
}));

function PieCenterLabel({ children }: { children: React.ReactNode }) {
    const { width, height, left, top } = useDrawingArea();
    return (
        <StyledText x={left + width / 2} y={top + height / 2}>
            {children}
        </StyledText>
    );
}

const CustomLegend = ({ text, value, color }: any) => {
    return (
        <SingleLegendWrapper>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <LabelDot color={color} />
                {text}
            </div>
            {value} (đơn hàng)
        </SingleLegendWrapper>
    );
};

const CustomLegends = ({ orders }: any) => {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {orders.map((order: any) => (
                <CustomLegend text={order.label} value={order.value} color={order.color} />
            ))}
        </div>
    );
};

interface SummaryData {
    status: string;
    count: number;
}

const getVietnameseLabel = (status: string) => {
    // Handle SALE_ORDER_STATUS (new API)
    switch (status) {
        case SALE_ORDER_STATUS.OPEN:
            return "Mở";
        case SALE_ORDER_STATUS.LOGISTIC_REVIEWED:
            return "Hậu cần xử lý";
        case SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING:
            return "Lãnh vật tư & Sản xuất";
        case SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING:
            return "Nhập kho & Xuất kho";
        case SALE_ORDER_STATUS.COMPLETED:
            return "Hoàn thành";
        case SALE_ORDER_STATUS.CANCELLED:
            return "Đã Huỷ";
    }
};

export const PieChartComponent = () => {
    const [{ data, error, isLoading }, fetchSummaryData] = api.get("/dashboard/sales-orders/summary/", {
        year: new Date().getFullYear(),
    });

    const getColorByStatus = (status: string) => {
        // Handle SALE_ORDER_STATUS (new API)
        switch (status) {
            case SALE_ORDER_STATUS.OPEN:
                return "#2196F3"; // Blue
            case SALE_ORDER_STATUS.LOGISTIC_REVIEWED:
                return "#FF9800"; // Orange
            case SALE_ORDER_STATUS.MATERIAL_REQUESTED_AND_MANUFACTURING:
                return "#FFC107"; // Amber
            case SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING:
                return "#00BCD4"; // Cyan
            case SALE_ORDER_STATUS.COMPLETED:
                return "#4CAF50"; // Green
            case SALE_ORDER_STATUS.CANCELLED:
                return "#F44336"; // Red
        } 
        // Default color for unknown statuses
        return "#757575"; // Grey
    };

    if (isLoading) return <PageLoader />;

    const orders = data.map((item: SummaryData) => ({
        ...item,
        label: getVietnameseLabel(item.status),
        value: item.count,
        color: getColorByStatus(item.status),
    }));

    const totalCount = orders.reduce((sum: any, item: any) => sum + item.value, 0);

    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            {orders.length > 0 ? (
                <PieChart
                    series={[{ data: orders, innerRadius: 80 }]}
                    {...size}
                    slotProps={{
                        legend: {
                            hidden: true,
                        },
                    }}
                >
                    <PieCenterLabel>{totalCount} Đơn hàng</PieCenterLabel>
                </PieChart>
            ) : (
                <PieCenterLabel>{totalCount} Đơn hàng</PieCenterLabel>
            )}

            <CustomLegends orders={orders} />
        </div>
    );
};
